/**
 * D3SecondaryDataSpriteManager
 * 
 * Система отрисовки вторичных данных в виде зеленых спрайтов на внешнем слое.
 * Наследует архитектуру от D3PlanetSpriteManager но без анимации.
 * 
 * Особенности:
 * - Зеленые спрайты с внешним позиционированием
 * - Статичная отрисовка (без анимации)
 * - Подписка на события для управления отображением
 * - Перпендикулярное смещение в противоположную сторону от основных планет
 */

import { devLog, devWarn } from '../../../../../utils/devLog';
import * as d3 from 'd3';
import { PlanetSprite } from './D3PlanetAnimator';
import { SECONDARY_DATA_SPRITE_CONFIG } from '../config/PlanetLayerConfig';
import { globalEventStream, createEvent } from '../core/EventStream';
import { GrahaPosition } from '../../../../../models/types';

/**
 * Интерфейс для конфигурации вторичного спрайта
 */
export interface SecondaryDataSpriteConfig {
  radius: number;
  strokeWidth: number;
  strokeColor: string;
  fillColor: string;
  fontSize: number;
  textColor: string;
  connectionLineLength: number;
  connectionLineWidth: number;
  externalOffset: number;
}

/**
 * Данные для отрисовки вторичных спрайтов
 */
export interface SecondaryDataInput {
  data: GrahaPosition[];
  visible: boolean;
  label?: string; // опциональная метка для набора данных
}

/**
 * События для управления вторичными данными
 */
export interface SecondaryDataEvents {
  'secondary.data.show': SecondaryDataInput;
  'secondary.data.hide': { label?: string };
  'secondary.data.update': SecondaryDataInput;
  'secondary.data.clear': {};
}

/**
 * Менеджер спрайтов вторичных данных
 */
export class D3SecondaryDataSpriteManager {
  private svg: d3.Selection<SVGSVGElement, unknown, any, any>;
  private spriteGroup!: d3.Selection<SVGGElement, unknown, any, any>; // Initialized in initializeSpriteGroup
  private sprites: Map<string, PlanetSprite> = new Map();
  private isVisible: boolean = true;
  private subscriptions: { unsubscribe: () => void }[] = [];

  // Конфигурация по умолчанию
  private readonly DEFAULT_CONFIG: SecondaryDataSpriteConfig = {
    radius: SECONDARY_DATA_SPRITE_CONFIG.RADIUS,
    strokeWidth: SECONDARY_DATA_SPRITE_CONFIG.STROKE_WIDTH,
    strokeColor: SECONDARY_DATA_SPRITE_CONFIG.STROKE_COLOR,
    fillColor: SECONDARY_DATA_SPRITE_CONFIG.FILL_COLOR,
    fontSize: SECONDARY_DATA_SPRITE_CONFIG.FONT_SIZE,
    textColor: SECONDARY_DATA_SPRITE_CONFIG.TEXT_COLOR,
    connectionLineLength: SECONDARY_DATA_SPRITE_CONFIG.CONNECTION_LINE_LENGTH,
    connectionLineWidth: SECONDARY_DATA_SPRITE_CONFIG.CONNECTION_LINE_WIDTH,
    externalOffset: SECONDARY_DATA_SPRITE_CONFIG.EXTERNAL_OFFSET
  };

  constructor(svg: d3.Selection<SVGSVGElement, unknown, any, any>) {
    this.svg = svg;
    this.initializeSpriteGroup();
    this.setupEventSubscriptions();
    
    if (SECONDARY_DATA_SPRITE_CONFIG.DEBUG_MODE) {
      devLog('🟢 [D3SecondaryDataSpriteManager] Initialized with config:', this.DEFAULT_CONFIG);
    }
  }

  /**
   * Инициализация группы для вторичных спрайтов
   */
  private initializeSpriteGroup(): void {
    // Найти существующую группу или создать новую
    let existingGroup = this.svg.select(`.${SECONDARY_DATA_SPRITE_CONFIG.GROUP_CLASS}`);
    
    if (existingGroup.empty()) {
      // ИСПРАВЛЕНИЕ: Найти правильную структуру DOM - viewport-content.main-viewport
      const mainViewport = this.svg.select('.viewport-content.main-viewport');
      
      if (!mainViewport.empty()) {
        // Найти background-group внутри main-viewport
        const backgroundGroup = mainViewport.select('.background-group');
        
        if (!backgroundGroup.empty()) {
          // Вставить сразу после background-group
          this.spriteGroup = mainViewport
            .insert('g', '.background-group + *')
            .classed(SECONDARY_DATA_SPRITE_CONFIG.GROUP_CLASS, true)
            .attr('data-layer', 'secondary-data-sprites');
            
          if (SECONDARY_DATA_SPRITE_CONFIG.DEBUG_MODE) {
            devLog('🟢 [D3SecondaryDataSpriteManager] Created sprite group after background-group in main-viewport');
          }
        } else {
          // Если background-group не найден, добавить в начало main-viewport
          this.spriteGroup = mainViewport
            .insert('g', ':first-child')
            .classed(SECONDARY_DATA_SPRITE_CONFIG.GROUP_CLASS, true)
            .attr('data-layer', 'secondary-data-sprites');
            
          if (SECONDARY_DATA_SPRITE_CONFIG.DEBUG_MODE) {
            devLog('🟢 [D3SecondaryDataSpriteManager] Created sprite group at start of main-viewport (no background-group found)');
          }
        }
      } else {
        // Fallback: найти viewport-content без main-viewport класса
        const viewportContent = this.svg.select('.viewport-content');
        
        if (!viewportContent.empty()) {
          const backgroundGroup = viewportContent.select('.background-group');
          
          if (!backgroundGroup.empty()) {
            this.spriteGroup = viewportContent
              .insert('g', '.background-group + *')
              .classed(SECONDARY_DATA_SPRITE_CONFIG.GROUP_CLASS, true)
              .attr('data-layer', 'secondary-data-sprites');
          } else {
            this.spriteGroup = viewportContent
              .insert('g', ':first-child')
              .classed(SECONDARY_DATA_SPRITE_CONFIG.GROUP_CLASS, true)
              .attr('data-layer', 'secondary-data-sprites');
          }
          
          if (SECONDARY_DATA_SPRITE_CONFIG.DEBUG_MODE) {
            devLog('🟢 [D3SecondaryDataSpriteManager] Created sprite group in viewport-content (fallback)');
          }
        } else {
          // Last fallback: добавить в конец SVG
          this.spriteGroup = this.svg
            .append('g')
            .classed(SECONDARY_DATA_SPRITE_CONFIG.GROUP_CLASS, true)
            .attr('data-layer', 'secondary-data-sprites');
            
          if (SECONDARY_DATA_SPRITE_CONFIG.DEBUG_MODE) {
            devLog('🟢 [D3SecondaryDataSpriteManager] Created sprite group in SVG root (last fallback)');
          }
        }
      }
    } else {
      this.spriteGroup = existingGroup as any;
      
      if (SECONDARY_DATA_SPRITE_CONFIG.DEBUG_MODE) {
        devLog('🟢 [D3SecondaryDataSpriteManager] Using existing sprite group');
      }
    }
  }

  /**
   * Настройка подписок на события
   */
  private setupEventSubscriptions(): void {
    // Подписка на показ вторичных данных
    const showSubscription = globalEventStream.subscribe('secondary.data.show', (event: any) => {
      this.handleShowSecondaryData(event);
    });
    this.subscriptions.push(showSubscription);

    // Подписка на скрытие вторичных данных
    const hideSubscription = globalEventStream.subscribe('secondary.data.hide', (event: any) => {
      this.handleHideSecondaryData(event);
    });
    this.subscriptions.push(hideSubscription);

    // Подписка на обновление вторичных данных
    const updateSubscription = globalEventStream.subscribe('secondary.data.update', (event: any) => {
      this.handleUpdateSecondaryData(event);
    });
    this.subscriptions.push(updateSubscription);

    // Подписка на очистку вторичных данных
    const clearSubscription = globalEventStream.subscribe('secondary.data.clear', (event: any) => {
      this.handleClearSecondaryData();
    });
    this.subscriptions.push(clearSubscription);

    // Подписка на очистку основного чарта
    const chartClearSubscription = globalEventStream.subscribe('chart.data.cleared', (event: any) => {
      this.handleClearSecondaryData();
    });
    this.subscriptions.push(chartClearSubscription);
  }

  /**
   * Создание спрайта вторичных данных
   */
  public createSecondaryDataSprite(
    planetName: string,
    containerIndex: number,
    initialPosition: { x: number, y: number },
    config: Partial<SecondaryDataSpriteConfig> = {}
  ): PlanetSprite {
    const finalConfig = { ...this.DEFAULT_CONFIG, ...config };
    
    // Создать контейнер спрайта
    const container = this.spriteGroup
      .append('g')
      .classed(SECONDARY_DATA_SPRITE_CONFIG.SPRITE_CLASS, true)
      .attr('data-planet', planetName)
      .attr('data-house', containerIndex.toString())
      .attr('data-type', 'secondary')
      .attr('transform', `translate(${initialPosition.x}, ${initialPosition.y})`);

    // Создать группу для соединительной линии
    const connectionGroup = container
      .append('g')
      .classed('connection-group', true);

    // Создать соединительную линию
    const connectionLine = connectionGroup
      .append('line')
      .classed(SECONDARY_DATA_SPRITE_CONFIG.LINE_CLASS, true)
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', -finalConfig.connectionLineLength)
      .attr('stroke', finalConfig.strokeColor)
      .attr('stroke-width', finalConfig.connectionLineWidth)
      .attr('opacity', 1);

    // Создать круг планеты
    const circle = container
      .append('circle')
      .classed(SECONDARY_DATA_SPRITE_CONFIG.CIRCLE_CLASS, true)
      .attr('r', finalConfig.radius)
      .attr('fill', finalConfig.fillColor)
      .attr('stroke', finalConfig.strokeColor)
      .attr('stroke-width', finalConfig.strokeWidth)
      .attr('opacity', 1);

    // Создать текст планеты
    const text = container
      .append('text')
      .classed(SECONDARY_DATA_SPRITE_CONFIG.TEXT_CLASS, true)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', finalConfig.fontSize)
      .attr('font-weight', 'bold')
      .attr('fill', finalConfig.textColor)
      .attr('opacity', 1)
      .text(planetName);

    // Создать объект спрайта
    const sprite: PlanetSprite = {
      container,
      connectionGroup,
      circle,
      text,
      connectionLine,
      position: initialPosition,
      rotation: 0,
      side: 'top',
      planetName,
      containerIndex
    };

    // Сохранить спрайт в реестре
    this.sprites.set(planetName, sprite);

    if (SECONDARY_DATA_SPRITE_CONFIG.DEBUG_MODE) {
      devLog(`🟢 [D3SecondaryDataSpriteManager] Created secondary sprite for ${planetName} at container ${containerIndex}`);
    }

    return sprite;
  }

  /**
   * Получить спрайт по имени планеты
   */
  public getSprite(planetName: string): PlanetSprite | undefined {
    return this.sprites.get(planetName);
  }

  /**
   * Получить все спрайты
   */
  public getAllSprites(): Map<string, PlanetSprite> {
    return new Map(this.sprites);
  }

  /**
   * Показать/скрыть все вторичные спрайты
   */
  public setVisible(visible: boolean): void {
    this.isVisible = visible;
    this.spriteGroup.style('display', visible ? 'block' : 'none');
    
    if (SECONDARY_DATA_SPRITE_CONFIG.DEBUG_MODE) {
      devLog(`🟢 [D3SecondaryDataSpriteManager] Visibility set to: ${visible}`);
    }
  }

  /**
   * Очистить все спрайты
   */
  public clearAllSprites(): void {
    this.clearAllSpritesForce();
  }

  /**
   * Получить статистику спрайтов
   */
  public getSpriteStats(): any {
    return {
      totalSprites: this.sprites.size,
      visible: this.isVisible,
      config: this.DEFAULT_CONFIG,
      spriteNames: Array.from(this.sprites.keys())
    };
  }

  // Флаг для предотвращения дублирования
  private isProcessingData: boolean = false;
  private lastDataHash: string = '';

  /**
   * Обработчик показа вторичных данных
   */
  private handleShowSecondaryData(event: any): void {
    const { data, visible, label } = event as SecondaryDataInput;
    
    // Создать хеш данных для предотвращения дублирования
    const dataHash = this.createDataHash(data, visible, label);
    
    if (SECONDARY_DATA_SPRITE_CONFIG.DEBUG_MODE) {
      devLog(`🟢 [D3SecondaryDataSpriteManager] Show secondary data:`, { 
        dataCount: data.length, 
        visible, 
        label,
        dataHash,
        lastDataHash: this.lastDataHash,
        isProcessing: this.isProcessingData
      });
    }

    // Проверить на дублирование данных
    if (this.isProcessingData && dataHash === this.lastDataHash) {
      if (SECONDARY_DATA_SPRITE_CONFIG.DEBUG_MODE) {
        devLog(`🟢 [D3SecondaryDataSpriteManager] Ignoring duplicate data processing`);
      }
      return;
    }

    // Установить флаг обработки
    this.isProcessingData = true;
    this.lastDataHash = dataHash;

    // Очистить существующие спрайты с принудительной очисткой DOM
    this.clearAllSpritesForce();

    // Установить видимость
    this.setVisible(visible);

    if (visible && data.length > 0) {
      // Создать спрайты для каждого элемента данных
      // Позиционирование будет выполнено через ElementRenderingEngine
      
      // Добавить небольшую задержку для полной очистки DOM
      setTimeout(() => {
        // Эмитировать событие о готовности к позиционированию
        globalEventStream.emit(createEvent(
          'secondary.data.ready.for.positioning',
          'd3-secondary-data-sprite-manager',
          { data, label }
        ));
        
        // Сбросить флаг обработки через небольшую задержку
        setTimeout(() => {
          this.isProcessingData = false;
        }, 100);
      }, 10);
    } else {
      // Сбросить флаг обработки
      this.isProcessingData = false;
    }
  }

  /**
   * Создать хеш данных для предотвращения дублирования
   */
  private createDataHash(data: any[], visible: boolean, label?: string): string {
    const dataStr = JSON.stringify(data);
    return `${dataStr}_${visible}_${label || 'no-label'}`;
  }

  /**
   * Принудительная очистка всех спрайтов с проверкой DOM
   */
  private clearAllSpritesForce(): void {
    // Очистить реестр спрайтов
    this.sprites.clear();
    
    // Принудительно удалить все элементы из DOM
    this.spriteGroup.selectAll('*').remove();
    
    // Дополнительная проверка - удалить все элементы с нашими классами
    this.spriteGroup.selectAll(`.${SECONDARY_DATA_SPRITE_CONFIG.SPRITE_CLASS}`).remove();
    
    if (SECONDARY_DATA_SPRITE_CONFIG.DEBUG_MODE) {
      const remainingElements = this.spriteGroup.selectAll('*').size();
      devLog(`🟢 [D3SecondaryDataSpriteManager] Force cleared all sprites, remaining elements: ${remainingElements}`);
    }
  }

  /**
   * Обработчик скрытия вторичных данных
   */
  private handleHideSecondaryData(event: any): void {
    const { label } = event;
    
    if (SECONDARY_DATA_SPRITE_CONFIG.DEBUG_MODE) {
      devLog(`🟢 [D3SecondaryDataSpriteManager] Hide secondary data:`, { label });
    }

    this.setVisible(false);
  }

  /**
   * Обработчик обновления вторичных данных
   */
  private handleUpdateSecondaryData(event: any): void {
    // Обновление = очистка + показ новых данных
    this.handleShowSecondaryData(event);
  }

  /**
   * Обработчик очистки вторичных данных
   */
  private handleClearSecondaryData(): void {
    if (SECONDARY_DATA_SPRITE_CONFIG.DEBUG_MODE) {
      devLog('🟢 [D3SecondaryDataSpriteManager] Clear secondary data');
    }

    // Сбросить флаги обработки
    this.isProcessingData = false;
    this.lastDataHash = '';
    
    this.clearAllSprites();
  }

  /**
   * Освобождение ресурсов
   */
  public dispose(): void {
    // Отписаться от событий
    this.subscriptions.forEach(subscription => {
      try {
        subscription.unsubscribe();
      } catch (error) {
        devWarn('🟢 [D3SecondaryDataSpriteManager] Error unsubscribing:', error);
      }
    });
    this.subscriptions.length = 0;

    // Очистить спрайты
    this.clearAllSprites();

    // Удалить группу
    if (this.spriteGroup && !this.spriteGroup.empty()) {
      this.spriteGroup.remove();
    }

    if (SECONDARY_DATA_SPRITE_CONFIG.DEBUG_MODE) {
      devLog('🟢 [D3SecondaryDataSpriteManager] Disposed');
    }
  }
}

// Export для консольной отладки
if (typeof window !== 'undefined') {
  (window as any).D3SecondaryDataSpriteManager = D3SecondaryDataSpriteManager;
}